/**
 * Scene-object restore helper.
 *
 * Undo/session/file restore paths rebuild `SceneObject`s from detached
 * snapshots or decoded persistence documents. Reusing the existing live
 * object when the restored value is semantically unchanged preserves caches
 * and memoization keyed by object identity, while still materializing fresh
 * objects for changed/new entries so restored state never aliases mutable
 * snapshot buffers by accident.
 */

import type { GeneratorRef } from "../modeling/generators/types.ts";
import { equalTopology } from "../modeling/topology.ts";
import type { CurveData, SceneObject } from "../modeling/types.ts";
import type { SceneObjectMap } from "./sceneObjectMap.ts";

export interface RestoreSceneObjectsOptions {
  /**
   * Lazily materialize a restored object for insertion into the live map.
   * Called only when the current live instance cannot be reused.
   */
  materialize?: (restored: SceneObject) => SceneObject;
}

type NumericArray = ArrayLike<number>;

function equalNumericArray(
  a?: NumericArray | null,
  b?: NumericArray | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if ((a as object).constructor !== (b as object).constructor) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function equalSetNumbers(
  a?: ReadonlySet<number> | null,
  b?: ReadonlySet<number> | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value == null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function equalStructuredValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return Object.is(a, b);

  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray || bIsArray) {
    if (!aIsArray || !bIsArray) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!equalStructuredValue(a[i], b[i])) return false;
    }
    return true;
  }

  if (!isPlainObject(a) || !isPlainObject(b)) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.hasOwn(b, key)) return false;
    if (!equalStructuredValue(a[key], b[key])) return false;
  }
  return true;
}

// Topology equality is owned by `modeling/topology.ts` so every per-edge
// attribute (flags, creases, future weights) stays in lockstep with its
// clone / canonicalize / concat counterparts.
const equalPolygonTopology = equalTopology;

function equalCurveData(a?: CurveData | null, b?: CurveData | null): boolean {
  return equalStructuredValue(a, b);
}

function equalMaterialSlots(
  a?: SceneObject["materialSlots"] | null,
  b?: SceneObject["materialSlots"] | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
    if (a[i].materialId !== b[i].materialId) return false;
  }
  return true;
}

function equalVertexGroups(
  a?: SceneObject["vertexGroups"] | null,
  b?: SceneObject["vertexGroups"] | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
    if (a[i].name !== b[i].name) return false;
    if (!equalSetNumbers(a[i].indices, b[i].indices)) return false;
  }
  return true;
}

function equalFaceAttributes(
  a?: SceneObject["faceAttributes"] | null,
  b?: SceneObject["faceAttributes"] | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (!equalNumericArray(a.materialIndex, b.materialIndex)) return false;
  if (!equalNumericArray(a.shading, b.shading)) return false;
  const ag = a.groups;
  const bg = b.groups;
  if (ag === bg) return true;
  if (!ag || !bg) return ag === bg;
  if (ag.length !== bg.length) return false;
  for (let i = 0; i < ag.length; i++) {
    if (ag[i].id !== bg[i].id) return false;
    if (ag[i].name !== bg[i].name) return false;
    if (!equalSetNumbers(ag[i].faces, bg[i].faces)) return false;
  }
  return true;
}

function equalGeneratorRef(
  a?: GeneratorRef | null,
  b?: GeneratorRef | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  return (
    a.generatorId === b.generatorId &&
    a.worldSpace === b.worldSpace &&
    a.geometrySignature === b.geometrySignature &&
    equalStructuredValue(a.params, b.params)
  );
}

export function equalSceneObjectForRestore(
  live: SceneObject,
  restored: SceneObject,
): boolean {
  if (live === restored) return true;

  if (live.id !== restored.id) return false;
  if (live.name !== restored.name) return false;
  if (live.objectKind !== restored.objectKind) return false;
  if (!equalNumericArray(live.position, restored.position)) return false;
  if (!equalNumericArray(live.rotation, restored.rotation)) return false;
  if (!equalNumericArray(live.scale, restored.scale)) return false;
  if (!equalNumericArray(live.vertices, restored.vertices)) return false;
  if (!equalNumericArray(live.indices, restored.indices)) return false;
  if (!equalNumericArray(live.loopNormals, restored.loopNormals)) return false;
  if (!equalPolygonTopology(live.topology, restored.topology)) return false;
  if (!equalNumericArray(live.uvs, restored.uvs)) return false;
  if (!equalNumericArray(live.baseVertices, restored.baseVertices))
    return false;
  if (!equalNumericArray(live.skinRadii, restored.skinRadii)) return false;
  if (!equalVertexGroups(live.vertexGroups, restored.vertexGroups))
    return false;
  if (!equalMaterialSlots(live.materialSlots, restored.materialSlots))
    return false;
  if (!equalFaceAttributes(live.faceAttributes, restored.faceAttributes))
    return false;
  if (live.shading !== restored.shading) return false;
  if (live.autoSmoothAngle !== restored.autoSmoothAngle) return false;
  if (!equalStructuredValue(live.light, restored.light)) return false;
  if (live.mannequinModelId !== restored.mannequinModelId) return false;
  if (!equalNumericArray(live.originOffset, restored.originOffset))
    return false;
  if (live.imported !== restored.imported) return false;
  if (!equalCurveData(live.curveData, restored.curveData)) return false;
  if (!equalStructuredValue(live.imagePlane, restored.imagePlane)) return false;
  if (!equalGeneratorRef(live.generator, restored.generator)) return false;

  return true;
}

/**
 * Restore a scene-object map while preserving live object identity for
 * entries whose content is unchanged.
 *
 * The returned map preserves the restored iteration order. When the entire
 * restored sequence matches the current map (same ids, same order, same
 * reused object refs), the original map is returned unchanged.
 */
export function restoreSceneObjectMap(
  current: SceneObjectMap,
  restoredObjects: readonly SceneObject[],
  options: RestoreSceneObjectsOptions = {},
): SceneObjectMap {
  const materialize =
    options.materialize ?? ((restored: SceneObject) => restored);
  const next = new Map<string, SceneObject>();
  let changed = current.size !== restoredObjects.length;
  const currentIter = current.entries();

  for (const restored of restoredObjects) {
    const live = current.get(restored.id);
    const value =
      live && equalSceneObjectForRestore(live, restored)
        ? live
        : materialize(restored);
    next.set(restored.id, value);

    const currentEntry = currentIter.next();
    if (
      currentEntry.done ||
      currentEntry.value[0] !== restored.id ||
      currentEntry.value[1] !== value
    ) {
      changed = true;
    }
  }

  if (!currentIter.next().done) changed = true;
  return changed ? next : current;
}
